import { useEffect, useRef } from 'react';

import { apiUrl } from '~shared/api';

const BACKOFF_INITIAL_DELAY_MS = 1000;
const BACKOFF_MAX_DELAY_MS = 30_000;

type UseNotificationsSocketParams<TMessage> = {
  pharmacyId?: string;
  onMessage: (data: TMessage) => void;
};

export const useNotificationsSocket = <TMessage>({
  pharmacyId,
  onMessage,
}: UseNotificationsSocketParams<TMessage>) => {
  const socketRef = useRef<WebSocket | null>(null);
  const onMessageRef = useRef(onMessage);

  const currentReconnectDelayRef = useRef(BACKOFF_INITIAL_DELAY_MS);
  const reconnectTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const isDisconnectionIntentionalRef = useRef(false);

  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);

  useEffect(() => {
    if (!pharmacyId) {
      return;
    }

    isDisconnectionIntentionalRef.current = false;

    const createWebSocketConnection = () => {
      if (socketRef.current) {
        return;
      }

      const socket = new WebSocket(apiUrl(pharmacyId, '/notifications'));

      socket.addEventListener('open', () => {
        currentReconnectDelayRef.current = BACKOFF_INITIAL_DELAY_MS;
      });

      const processIncomingMessage = (event: MessageEvent) => {
        try {
          const parsedData = JSON.parse(event.data) as TMessage;

          onMessageRef.current(parsedData);
        } catch (error) {
          console.error('WebSocket message parsing error:', error);
        }
      };

      const handleConnectionClose = () => {
        if (socketRef.current !== socket) {
          return;
        }

        socketRef.current = null;

        if (!isDisconnectionIntentionalRef.current) {
          reconnectTimeoutIdRef.current = setTimeout(() => {
            createWebSocketConnection();
          }, currentReconnectDelayRef.current);

          currentReconnectDelayRef.current = Math.min(
            currentReconnectDelayRef.current * 2,
            BACKOFF_MAX_DELAY_MS
          );
        }
      };

      socket.addEventListener('message', processIncomingMessage);
      socket.addEventListener('close', handleConnectionClose);

      socket.addEventListener('error', (error) => {
        console.error('WebSocket connection error:', error);
      });

      socketRef.current = socket;
    };

    createWebSocketConnection();

    return () => {
      isDisconnectionIntentionalRef.current = true;

      if (reconnectTimeoutIdRef.current) {
        clearTimeout(reconnectTimeoutIdRef.current);
      }

      const activeSocket = socketRef.current;

      if (activeSocket) {
        if (activeSocket.readyState === WebSocket.OPEN) {
          activeSocket.close();
        } else if (activeSocket.readyState === WebSocket.CONNECTING) {
          activeSocket.addEventListener('open', () => {
            activeSocket.close();
          });
        }
        socketRef.current = null;
      }
    };
  }, [pharmacyId]);
};
